import React, { useEffect, useState } from "react";
import { Form, Input, Button, Typography, Modal, Spin } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import axios from "axios";
import { BACKEND_API_URL, FRONTEND_APP_URL } from "../../environment";
import { useParams, useNavigate } from "react-router-dom";

const { Title, Text } = Typography;

const UserPasswordChange = ({ location }) => {
  const { token } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const [name, setName] = useState("User");

  useEffect(() => {
    // const token = pathSpliter(location.pathname, 2) || "";

    if (!token) {
      setLoading(false);
      setTokenValid(false);
      setStatusMessage("No token provided.");
      return;
    }

    const validateToken = async () => {
      try {
        const res = await axios.post(
          `${BACKEND_API_URL}/validate-token-user-password`,
          {
            token,
          }
        );

        if (res.status === 200 && res.data?.data?.length > 0) {
          const user = res.data.data[0];
          form.setFieldsValue({
            email: user.email,
            tokenvalue: token,
          });

          setName(user.full_name);
          setTokenValid(true);
          setStatusMessage("Token is valid. You can set your password.");
        } else {
          setTokenValid(false);
          setStatusMessage("Invalid or expired token.");
        }
      } catch {
        setTokenValid(false);
        setStatusMessage("Request a new password reset link.");
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [location, form]);

  const validatePassword = (p) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,16}$/.test(
      p
    );

  const onFinish = async (values) => {
    if (!validatePassword(values.password))
      return Modal.warning({
        title:
          "Password must be 8–16 characters and include uppercase, lowercase, number & special character.",
      });

    if (values.password !== values.user_confirm_password)
      return Modal.warning({ title: "Passwords do not match." });

    try {
      const res = await axios.post(
        `${BACKEND_API_URL}/user-password-update-via-gmail`,
        values
      );

      if (res.data.error) return Modal.warning({ title: res.data.message });

      Modal.success({
        title: res.data.message || "Password updated successfully!",
      });

      setPasswordUpdated(true);
      form.resetFields();

      // End any session already open in this browser, then go to login.
      localStorage.removeItem("token");
      localStorage.removeItem("auth_info");
      sessionStorage.clear();
      setTimeout(() => window.location.replace("/login"), 2000);
    } catch (err) {
      Modal.error({
        title: "Something went wrong",
        content: err.response?.data?.message || "Please try again later.",
      });
    }
  };

  return (
    <div className="password-wrapper">
      {loading ? (
        <Spin tip="Validating token..." size="large" />
      ) : (
        <div className="password-card">
          <div className="header">
            <Title level={2} className="gradient-title">
              Set Password
            </Title>
            {!passwordUpdated && (
              <Text
                type={!tokenValid ? "danger" : "secondary"}
                className="status-text"
              >
                {statusMessage}
              </Text>
            )}
          </div>

          {!passwordUpdated && tokenValid && (
            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Text strong>Hello {name}! Please set your new password.</Text>

              <Form.Item name="email" hidden>
                <Input />
              </Form.Item>

              <Form.Item name="tokenvalue" hidden>
                <Input />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[{ required: true, message: "Password is required" }]}
              >
                <Input.Password
                  placeholder="Enter new password"
                  iconRender={(vis) =>
                    vis ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                />
              </Form.Item>

              {/* <Text type="secondary" className="hint-text">
                Password must be 8–16 characters.
                <br />
                Must include uppercase, lowercase, number & symbol.
              </Text> */}

              <Text type="secondary" className="hint-text">
                Password must be 8–16 characters.
                <br />
                Include at least one uppercase letter, one lowercase letter,
                <br />
                one number, and one special character
                <br />
                <strong>@ $ ! % * ? &</strong>
              </Text>

              <Form.Item
                label="Confirm Password"
                name="user_confirm_password"
                dependencies={["password"]}
                rules={[
                  { required: true, message: "Confirm your password" },
                  ({ getFieldValue }) => ({
                    validator(_, v) {
                      return !v || getFieldValue("password") === v
                        ? Promise.resolve()
                        : Promise.reject("Passwords do not match.");
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder="Confirm password"
                  iconRender={(vis) =>
                    vis ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                className="submit-btn"
              >
                Save Password
              </Button>
            </Form>
          )}

          {passwordUpdated && (
            <div className="success-message">
              <h3 style={{ color: "green", fontWeight: 600 }}>
                🎉 Password Updated Successfully!
              </h3>
            </div>
          )}
        </div>
      )}

      {/* CSS */}
      <style>{`
            .password-wrapper {
                min-height: 100vh;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 20px;
            }

            .password-card {
                width: clamp(300px, 85%, 420px);
                background: #fff;
                padding: 30px;
                border-radius: 16px;
                box-shadow: 0px 8px 30px rgba(0,0,0,0.1);
                animation: fadeIn .4s;
            }

            .header {
                text-align: center;
                margin-bottom: 15px;
            }

            .gradient-title {
                background: linear-gradient(90deg,#6366f1,#8b5cf6,#3b82f6);
                -webkit-background-clip: text;
                color: transparent;
                font-weight: 800;
                font-size: 28px;
            }

            .submit-btn {
                background: linear-gradient(90deg,#6366f1,#8b5cf6,#3b82f6);
                border: none;
                border-radius: 10px;
                font-weight: 600;
            }

            .hint-text {
                font-size: 12px;
                margin-bottom: 8px;
                display: block;
            }

            .status-text {
                font-size: 14px;
                margin-top: 6px;
                display: block;
            }

            .success-message {
                text-align: center;
                padding: 20px;
                color: green;
            }
        `}</style>
    </div>
  );
};

export default UserPasswordChange;
